import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

// New schedule pieces
import ScheduleToolbar from "./ScheduleToolbar/ScheduleToolbar";
import TeamRail from "./TeamRail/TeamRail";
import TimelineBody, { computeTotalJobs } from "./TimelineBody/TimelineBody";
import ScheduleFooter from "./ScheduleFooter/ScheduleFooter";

import useNowTick from "./hooks/useNowTick";
import useDragAssign from "./hooks/useDragAssign";

import { resolveWorkdayWindow } from "./helpers/workday";
import { nowAsShopHourFloat, isSameShopDay } from "./helpers/time";

import { ScheduleStyles } from "./schedule.styles";

const Schedule = ({
  branchWorkers,
  timeZone,
  dateQuery,
  setDateQuery,
  includedStatuses,
  setIncludedStatuses,
  selectedWorkerIds,
  setSelectedWorkerIds
}) => {
  const { employees, workDay, unassignedServices } = useSelector(
    state => state.servicesReducer
  );

  const [railCollapsed, setRailCollapsed] = useState(false);
  const [collapsedWorkers, setCollapsedWorkers] = useState({});
  const [showUnassigned, setShowUnassigned] = useState(true);

  const {
    draggingId,
    draggingFromWorker,
    startDrag,
    endDrag,
    handleDrop,
    handleDropToUnassigned
  } = useDragAssign();
  const now = useNowTick();
  const nowH = nowAsShopHourFloat(timeZone, now);
  const isToday = isSameShopDay(now, dateQuery, timeZone);

  const workdayWindow = useMemo(() => resolveWorkdayWindow(workDay), [workDay]);

  // Map schedule data (WorkerSchedule, with services) by worker id so the
  // TeamRail can compute utilization and the TimelineBody can render rows.
  const scheduleById = useMemo(() => {
    const out = {};
    for (const w of employees || []) out[w.id] = w;
    return out;
  }, [employees]);

  // Workers shown in the rail are the full branch list (so the user can
  // toggle anyone in/out). We enrich each with service data + role from
  // the schedule response when available.
  const railWorkers = useMemo(() => {
    return (branchWorkers || []).map(w => {
      const sched = scheduleById[w.id];
      return sched
        ? {
            id: w.id,
            firstName: w.firstName || sched.firstName,
            lastName: w.lastName || sched.lastName,
            profileImage: w.profileImage || sched.profileImage,
            role: sched.role || null,
            inProgressServices: sched.inProgressServices,
            acceptedServices: sched.acceptedServices,
            pausedServices: sched.pausedServices,
            finishedServices: sched.finishedServices,
            canceledServices: sched.canceledServices
          }
        : {
            id: w.id,
            firstName: w.firstName,
            lastName: w.lastName,
            profileImage: w.profileImage,
            role: null
          };
    });
  }, [branchWorkers, scheduleById]);

  // Drive the timeline rows from the full branch list (not the schedule
  // response) so workers with zero services still get a row. The empty row
  // is also a valid drop target for unassigned tickets.
  const visibleWorkers = useMemo(() => {
    return (branchWorkers || [])
      .filter(w => selectedWorkerIds.includes(w.id))
      .map(w => {
        const sched = scheduleById[w.id];
        return sched
          ? sched
          : {
              id: w.id,
              firstName: w.firstName,
              lastName: w.lastName,
              profileImage: w.profileImage,
              role: null,
              inProgressServices: null,
              acceptedServices: null,
              pausedServices: null,
              finishedServices: null,
              canceledServices: null
            };
      });
  }, [branchWorkers, selectedWorkerIds, scheduleById]);

  const toggleWorker = id =>
    setSelectedWorkerIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const toggleWorkerCollapse = id =>
    setCollapsedWorkers(c => ({ ...c, [id]: !c[id] }));

  const totalJobs = useMemo(
    () => computeTotalJobs(visibleWorkers, includedStatuses),
    [visibleWorkers, includedStatuses]
  );

  return (
    <ScheduleStyles $railCollapsed={railCollapsed}>
      <TeamRail
        workers={railWorkers}
        selectedIds={selectedWorkerIds}
        onToggleWorker={toggleWorker}
        onSelectAll={() =>
          setSelectedWorkerIds((branchWorkers || []).map(w => w.id))
        }
        onClearAll={() => setSelectedWorkerIds([])}
        collapsed={railCollapsed}
        onCollapse={() => setRailCollapsed(true)}
        onExpand={() => setRailCollapsed(false)}
        workdayHours={workdayWindow.totalH}
      />

      <div className="main-card">
        <ScheduleToolbar
          dateQuery={dateQuery}
          setDateQuery={setDateQuery}
          includedStatuses={includedStatuses}
          setIncludedStatuses={setIncludedStatuses}
          showUnassigned={showUnassigned}
          setShowUnassigned={setShowUnassigned}
          unassignedCount={unassignedServices?.length || 0}
          employees={employees}
        />

        <TimelineBody
          visibleWorkers={visibleWorkers}
          unassignedServices={unassignedServices || []}
          showUnassigned={showUnassigned}
          includedStatuses={includedStatuses}
          workdayWindow={workdayWindow}
          collapsedWorkers={collapsedWorkers}
          onToggleWorkerCollapse={toggleWorkerCollapse}
          draggingId={draggingId}
          draggingFromWorker={draggingFromWorker}
          startDrag={startDrag}
          endDrag={endDrag}
          handleDrop={handleDrop}
          handleDropToUnassigned={handleDropToUnassigned}
          nowH={nowH}
          isToday={isToday}
          dateQuery={dateQuery}
        />

        <ScheduleFooter
          nowH={nowH}
          workersCount={visibleWorkers.length}
          jobsCount={totalJobs}
        />
      </div>

      {/* Ticket drawer + workers modal are rendered globally
          (GlobalServiceDrawers in AppShell) — TimelineBody's
          openTicketDetails drives them through redux. */}
    </ScheduleStyles>
  );
};

export default Schedule;
